// World state and entity pools.
// Conforms to GDD Appendix A §A.4.

import type { Player, Enemy, Projectile, Pickup, Hazard, WormholeState, BonusGameState } from '../types.js';
import { Pool } from './pool.js';
import { EventBus } from './events.js';
import { RngService } from './rng.js';
import { createTurretState, type TurretState } from './turret.js';
import { createWormholeState } from './wormhole.js';
import { createBonusGameState } from './bonus-game.js';

function createPlayer(index: number): Player {
  return {
    id: 0,
    kind: 'player',
    pos: { x: 0, y: 0 },
    vel: { x: 0, y: 0 },
    hp: 100,
    max_hp: 100,
    hitbox_radius: 0.6,
    faction: 'player',
    status_effects: [],
    alive: false,
    spawn_tick: 0,
    targetable: true,
    pool_index: index,
    shield: 0,
    max_shield: 0,
    hull: 0,
    max_hull: 0,
    // Far past so shield regen is never blocked at spawn (shield is 0 anyway).
    last_damage_tick: -1_000_000,
    lumensurge_tier: 1,
    weapons: [],
    // Pre-roll fixture default: a fully-fitted hull. Every run re-rolls this
    // from the ship seed in restart()/applyLoadout (rollWeaponSlots) before
    // any grant path can read it.
    weapon_slots: 4,
    passives: [],
    active: null,
    dash: { active: false, dir: { x: 0, y: 0 }, iframes_until: 0, cooldown_until: 0, charges: 1, recharge_until: 0 },
    mercy_until: 0,
    focus_aim: { held: false, dir: { x: 0, y: 1 } },
    rerolls_used: 0,
    boon_crit_chance: 0,
    boon_shield_regen_mult: 1,
    boon_move_speed_mult: 1
  };
}

function createEnemy(index: number): Enemy {
  return {
    id: 0,
    kind: 'enemy',
    pos: { x: 0, y: 0 },
    vel: { x: 0, y: 0 },
    hp: 1,
    max_hp: 1,
    hitbox_radius: 0.7,
    faction: 'enemy',
    status_effects: [],
    alive: false,
    spawn_tick: 0,
    targetable: false,
    pool_index: index,
    def_id: '',
    elite: false,
    affixes: [],
    threat_score: 0,
    steering: { weights: [0, 0, 0, 0, 0, 0], formation_slot: 0, goal: { x: 0, y: 0 } },
    age_ticks: 0,
    telegraph_until: 0,
    plane: 1
  };
}

function createProjectile(index: number): Projectile {
  return {
    id: 0,
    kind: 'projectile',
    pos: { x: 0, y: 0 },
    vel: { x: 0, y: 0 },
    hp: 1,
    max_hp: 1,
    hitbox_radius: 0.25,
    faction: 'ally',
    status_effects: [],
    alive: false,
    spawn_tick: 0,
    targetable: false,
    pool_index: index,
    weapon_id: '',
    damage_snapshot: 0,
    penetration_left: 0,
    ricochet_left: 0,
    expire_tick: 0,
    hit_ids: []
  };
}

function createPickup(index: number): Pickup {
  return {
    id: 0,
    kind: 'pickup',
    pos: { x: 0, y: 0 },
    vel: { x: 0, y: 0 },
    hp: 1,
    max_hp: 1,
    hitbox_radius: 0.3,
    faction: 'neutral',
    status_effects: [],
    alive: false,
    spawn_tick: 0,
    targetable: false,
    pool_index: index,
    pickup_kind: 'xp_mote',
    value: 1,
    magnet: false
  };
}

function createHazard(index: number): Hazard {
  return {
    id: 0,
    kind: 'hazard',
    pos: { x: 0, y: 0 },
    vel: { x: 0, y: 0 },
    hp: 1,
    max_hp: 1,
    hitbox_radius: 0,
    faction: 'enemy',
    status_effects: [],
    alive: false,
    spawn_tick: 0,
    targetable: false,
    pool_index: index,
    hazard_id: '',
    zone: { shape: 'circle', r: 0 },
    arm_tick: 0,
    end_tick: 0,
    telegraph_until: 0
  };
}

export class WorldState {
  player: Player;
  /**
   * The ACTIVE plane's enemy pool. Every sim/render system reads this field,
   * so the wormhole subspace plane is contained by construction: entering
   * subspace re-points it at the dedicated subspace pool and the normal pool
   * becomes unreachable (never simulated, never rendered, never hit); leaving
   * re-points it back. The two pools never share an entity.
   */
  enemies: Pool<Enemy>;
  /** Normal-plane enemy pool (the arena's own). */
  private readonly normalEnemies: Pool<Enemy>;
  /**
   * Subspace-plane enemy pool (wormhole bonus level, sim/subspace.ts):
   * subspace attackers are allocated ONLY here. Cleared on exit — survivors
   * dissipate, they never follow the player home.
   */
  readonly subspaceEnemies: Pool<Enemy>;
  projectiles: Pool<Projectile>;
  pickups: Pool<Pickup>;
  hazards: Pool<Hazard>;
  tick = 0;
  runTime = 0;
  runSeed = 0;
  gameOver = false;
  outcome: 'victory' | 'defeat' | null = null;
  /** Hold-ground turret state (see turret.ts); reset per run. */
  turret: TurretState = createTurretState();
  /** Radius of the visible-area circle (half the smaller view dimension),
   * pushed by the renderer each frame. Target acquisition caps at this so
   * the ship never attacks enemies outside the visible area. */
  viewRadius = 22.5;
  /** Effective magnet pull radius in arena units (base × upgrade/trait/
   * Serenity multipliers), written by game.updatePickups every sim tick and
   * read by the renderer's magnet ring. 0 until the first pickup pass. */
  magnetPullRadius = 0;
  /** Wormhole spawner state (see wormhole.ts); reset per run. */
  wormhole: WormholeState = createWormholeState();
  /** Wormhole bonus game state (see bonus-game.ts); reset per run. */
  bonus: BonusGameState = createBonusGameState();
  // ---- Custom-event world flags (sim/events/*) ----
  // Optional additive fields written by custom events and read by other sim
  // systems with `?? 1` / truthiness so their absence is always a no-op.
  // Cleared on reset() so a flag from an event interrupted by a restart never
  // leaks into the next run (EventsManager.reset drops the active event
  // without calling end()).
  /** Swarm Surge: threat-accrual multiplier read by updateDirector (?? 1). */
  eventThreatMult?: number;
  /** Swarm Surge: forced edge (0–3) for stream formations while set. */
  eventStreamEdge?: number;
  /** Blood Moon: drop-doubling flag read by the kill path (kill.ts). */
  eventFlags?: { bloodMoon?: boolean };
  events: EventBus;
  rng: RngService;

  private idSource = { nextId: 1 };

  constructor(
    maxEnemies = 512,
    maxProjectiles = 1536,
    maxPickups = 512,
    maxHazards = 64
  ) {
    this.normalEnemies = new Pool(maxEnemies, createEnemy, this.idSource);
    // Subspace pool: formation waves field ≤ ~40 attackers at once, so a
    // quarter of the normal cap (floored at 64) is ample headroom.
    this.subspaceEnemies = new Pool(
      Math.max(64, Math.floor(maxEnemies / 4)),
      createEnemy,
      this.idSource
    );
    this.enemies = this.normalEnemies;
    this.projectiles = new Pool(maxProjectiles, createProjectile, this.idSource);
    this.pickups = new Pool(maxPickups, createPickup, this.idSource);
    this.hazards = new Pool(maxHazards, createHazard, this.idSource);
    this.player = createPlayer(-1);
    this.events = new EventBus();
    this.rng = new RngService(0);
  }

  reset(seed: number): void {
    this.tick = 0;
    this.runTime = 0;
    this.runSeed = seed >>> 0;
    this.gameOver = false;
    this.outcome = null;
    // Plane restore first: a restart mid-subspace must not leak subspace
    // attackers into the new run (or strand the normal pool off the field).
    this.enemies = this.normalEnemies;
    this.normalEnemies.clear();
    this.subspaceEnemies.clear();
    this.projectiles.clear();
    this.pickups.clear();
    this.hazards.clear();
    this.events.clear();
    this.rng = new RngService(this.runSeed);
    this.idSource.nextId = 1;

    this.player.alive = true;
    this.player.pos = { x: 0, y: 0 };
    this.player.vel = { x: 0, y: 0 };
    this.player.hp = this.player.max_hp;
    this.player.shield = 0;
    this.player.max_shield = 0;
    this.player.hull = 0;
    this.player.max_hull = 0;
    this.player.last_damage_tick = -1_000_000;
    this.turret = createTurretState();
    this.wormhole = createWormholeState();
    this.bonus = createBonusGameState();
    // Custom-event world flags (sim/events/*): never leak across runs.
    this.eventThreatMult = undefined;
    this.eventStreamEdge = undefined;
    this.eventFlags = undefined;
    this.player.lumensurge_tier = 1;
    this.player.weapons = [];
    this.player.passives = [];
    this.player.active = null;
    this.player.dash = { active: false, dir: { x: 0, y: 0 }, iframes_until: 0, cooldown_until: 0, charges: 1, recharge_until: 0 };
    this.player.mercy_until = 0;
    this.player.focus_aim = { held: false, dir: { x: 0, y: 1 } };
    this.player.rerolls_used = 0;
    this.player.boon_crit_chance = 0;
    this.player.boon_shield_regen_mult = 1;
    this.player.boon_move_speed_mult = 1;
    // Duck-typed boon fields: never leak across runs.
    this.player.dashChargeBonus = undefined;
    this.player.dashWakeDamage = undefined;
    this.player.dashWake = undefined;
  }

  /** True while the run is in the wormhole subspace plane. */
  get inSubspace(): boolean {
    return this.enemies !== this.normalEnemies;
  }

  /**
   * Switch the active plane to subspace (wormhole bonus entry): the normal
   * pool is detached untouched — suspended enemies keep their exact pos/vel
   * and resume byte-identical on exitSubspace().
   */
  enterSubspace(): void {
    if (this.inSubspace) return;
    this.enemies = this.subspaceEnemies;
  }

  /**
   * Return to the normal plane: subspace survivors dissipate (the subspace
   * pool is cleared) and the suspended normal pool becomes active again.
   */
  exitSubspace(): void {
    if (!this.inSubspace) return;
    this.subspaceEnemies.clear();
    this.enemies = this.normalEnemies;
  }

  getEntityById(id: number): { pos: { x: number; y: number }; alive: boolean } | undefined {
    if (this.player.id === id && this.player.alive) return this.player;
    for (const e of this.enemies.alive()) if (e.id === id) return e;
    for (const p of this.projectiles.alive()) if (p.id === id) return p;
    for (const p of this.pickups.alive()) if (p.id === id) return p;
    for (const h of this.hazards.alive()) if (h.id === id) return h;
    return undefined;
  }
}
